import { useState, useEffect } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { 
  AlertTriangle, 
  Trash2, 
  CheckCircle, 
  Users,
  Loader2,
  RefreshCw,
  Eye,
  ArrowRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCleanDuplicates = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  const [fixingLabels, setFixingLabels] = useState(false);
  const [fixLabelsResult, setFixLabelsResult] = useState(null);

  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/admin/cases/duplicates/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
    } catch (error) {
      toast.error("Error al cargar preview de duplicados");
    } finally {
      setLoading(false);
    }
  };

  const fixPersonLabels = async () => {
    setFixingLabels(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/admin/cases/fix-person-labels`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFixLabelsResult(response.data);
      toast.success(`¡Etiquetas corregidas! ${response.data.fixed_count} casos actualizados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al corregir etiquetas");
    } finally {
      setFixingLabels(false);
    }
  };

  const executeClean = async () => {
    if (!window.confirm(
      `¿Estás seguro de eliminar ${preview?.total_cases_to_delete || 0} casos duplicados?\n\n` +
      `Se mantendrá el caso más avanzado de cada cliente.\n\n` +
      `Esta acción NO se puede deshacer.`
    )) {
      return;
    }

    setCleaning(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/admin/cases/duplicates/clean`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCleanResult(response.data);
      toast.success(`¡Limpieza completada! ${response.data.deleted_count} casos eliminados`);
      fetchPreview(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la limpieza");
    } finally {
      setCleaning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Limpiar Casos Duplicados</h1>
          <p className="text-gray-500 mt-1">Identifica y elimina casos duplicados por cliente</p>
        </div>

        {/* Result Card */}
        {cleanResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
              <h2 className="text-xl font-semibold text-emerald-800">¡Limpieza Completada!</h2>
            </div>
            <p className="text-emerald-700 mb-4">
              Se eliminaron <strong>{cleanResult.deleted_count}</strong> casos duplicados.
            </p>
            {cleanResult.details && cleanResult.details.length > 0 && (
              <div className="bg-white rounded-lg p-4 max-h-60 overflow-y-auto">
                <h3 className="font-medium text-gray-900 mb-2">Detalles:</h3>
                {cleanResult.details.slice(0, 10).map((detail, idx) => (
                  <div key={idx} className="text-sm py-1 border-b border-gray-100 last:border-0">
                    <span className="text-gray-700">{detail.client_name}</span>
                    <span className="text-gray-400 mx-2">•</span>
                    <span className="text-red-500">Eliminado: paso {detail.was_at_step}</span>
                    <span className="text-gray-400 mx-2">→</span>
                    <span className="text-emerald-600">Mantenido: paso {detail.kept_at_step}</span>
                  </div>
                ))}
                {cleanResult.details.length > 10 && (
                  <p className="text-xs text-gray-400 mt-2">
                    ... y {cleanResult.details.length - 10} más
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        {!cleanResult && preview && (
          <>
            {preview.total_users_with_duplicates === 0 ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">¡Todo Limpio!</h2>
                <p className="text-gray-600">
                  No hay casos duplicados. Cada cliente tiene exactamente un caso.
                </p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Users className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Clientes con duplicados</p>
                        <p className="text-2xl font-bold text-amber-600">{preview.total_users_with_duplicates}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Trash2 className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Casos a eliminar</p>
                        <p className="text-2xl font-bold text-red-600">{preview.total_cases_to_delete}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Explanation */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">¿Qué hará la limpieza?</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500" />
                      Por cada cliente, se mantiene el caso <strong>más avanzado</strong> (mayor current_step)
                    </li>
                    <li className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-blue-500" />
                      Los casos duplicados se <strong>eliminan</strong> permanentemente
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-blue-500" />
                      También se eliminan los <strong>case_steps</strong> asociados
                    </li>
                  </ul>
                </div>

                {/* Duplicates List */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">Clientes con Casos Duplicados</h3>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                    {preview.duplicates.map((dup, idx) => (
                      <div key={idx} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-gray-900">{dup.client_name}</p>
                            <p className="text-sm text-gray-500">{dup.client_email}</p>
                          </div>
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                            {dup.total_cases} casos
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3">
                          {/* Cases to delete */}
                          <div className="flex-1">
                            <p className="text-xs text-red-500 font-medium mb-1">Eliminar:</p>
                            <div className="flex flex-wrap gap-1">
                              {dup.delete.map((del, i) => (
                                <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                  Paso {del.current_step}
                                </span>
                              ))}
                            </div>
                          </div>
                          
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                          
                          {/* Case to keep */}
                          <div>
                            <p className="text-xs text-emerald-500 font-medium mb-1">Mantener:</p>
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                              Paso {dup.keep.current_step}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    onClick={fetchPreview}
                    variant="outline"
                    disabled={cleaning}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualizar Preview
                  </Button>
                  <Button
                    onClick={executeClean}
                    disabled={cleaning || preview.total_cases_to_delete === 0}
                    className="bg-red-600 hover:bg-red-700 flex-1"
                  >
                    {cleaning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Limpiando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar {preview.total_cases_to_delete} Casos Duplicados
                      </>
                    )}
                  </Button>
                </div>

                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Importante</p>
                    <p className="text-sm text-amber-700">
                      Esta migración es permanente. Los vendedores podrán seguir ganando comisiones 
                      con el nuevo sistema de VIP Partners, pero ya no tendrán acceso al dashboard de vendedor anterior.
                    </p>
                  </div>
                </div>

                {/* Fix Labels Section */}
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mt-6">
                  <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Corregir Etiquetas de Personas
                  </h3>
                  <p className="text-sm text-purple-700 mb-4">
                    Si un cliente compró múltiples asesorías y los casos no tienen etiqueta 
                    "(Persona 1 de 4)", esta opción las agrega automáticamente.
                  </p>
                  
                  {fixLabelsResult && (
                    <div className="bg-white rounded-lg p-3 mb-4">
                      <p className="text-sm text-emerald-700">
                        ✅ Se actualizaron <strong>{fixLabelsResult.fixed_count}</strong> casos
                      </p>
                    </div>
                  )}
                  
                  <Button
                    onClick={fixPersonLabels}
                    disabled={fixingLabels}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  >
                    {fixingLabels ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Corrigiendo...
                      </>
                    ) : (
                      <>
                        <Users className="w-4 h-4 mr-2" />
                        Agregar Etiquetas de Persona
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminCleanDuplicates;
